import clsx from 'clsx';
import { mergeProps, useContext } from 'solid-js';
import { theme } from '../../style/theme';
import { SvgIconProps, SvgIconStyledProps } from './svg-icon.d';
import { AppContext } from '../../app-context-provider/app-context-provider';

const sizeMap: Record<string, number> = {
    xs: 0.9,        /* ~10px — DS breadcrumb chevron divider */
    small: 1.43,    /* ~16px */
    'sm-md': 1.64,  /* ~18px — DS sidebar parent-row chevron container */
    medium: 1.79,   /* ~20px */
    default: 2.14,  /* ~24px */
    lg: 2.55,       /* ~28px — DS icon-box size used in mobile menu utility rows */
    large: 2.86,    /* ~31px */
    xlarge: 4.3,    /* ~47px */
};

const defaultProps: SvgIconStyledProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    backgroundOpacity: 0,
    version: '1.1',
    x: '0px',
    y: '0px',
    viewBox: '0 0 30 30',
    height: 2.14,
    width: 2.14,
    padding: '',
    fill: theme.colors.svg.default,
    opacity: 1,
};

export const SvgIcon = (componentProps: SvgIconProps) => {
    const { localize } = useContext(AppContext);

    const props = mergeProps(defaultProps, componentProps);

    const getAltText = () => {
        if (componentProps.ariaLabel) {
            return componentProps.ariaLabel;
        }
        return localize(componentProps.defaultAriaLabel, componentProps.defaultAriaLabel);
    };

    const getStyles = (): Record<string, string> => {
        const styles: Record<string, string> = {};

        styles.fill = props.fill || 'inherit';
        if (props.stroke) styles.stroke = props.stroke;
        styles.cursor = (props.onClick || props.inButton) ? 'pointer' : 'inherit';

        const resolvedWidth = props.size
            ? `${sizeMap[props.size]}rem`
            : typeof props.width === 'number' ? `${props.width}rem` : String(props.width);
        const resolvedHeight = props.size
            ? `${sizeMap[props.size]}rem`
            : typeof props.height === 'number' ? `${props.height}rem` : String(props.height);

        styles.width = resolvedWidth;
        styles.height = props.fullHeight ? '100%' : resolvedHeight;
        styles['min-width'] = `${props.size ? sizeMap[props.size] : props.width}rem`;
        styles['min-height'] = `${props.size ? sizeMap[props.size] : props.height}rem`;
        styles.transition = 'fill 0.2s, stroke 0.2s';

        if (props.inButton) {
            styles.position = 'absolute';
            if (props.placement) styles[props.placement] = String(props.placementValue || 0);
            styles.top = '0';
            styles.bottom = '0';
        }

        if (props.shape === 'square') {
            styles.padding = props.padding || '1.7rem';
        }

        if (props.shape === 'circle') {
            styles.padding = props.padding || '1.2rem';
            styles['border-radius'] = '50%';
        }

        if (props.opacity !== undefined) styles.opacity = String(props.opacity);

        if (props.disabled) {
            styles.opacity = '0.5';
            styles.cursor = 'default';
        }

        if (props.transform) styles.transform = props.transform;

        return styles;
    };

    const parseCssString = (css: string): Record<string, string> => {
        const result: Record<string, string> = {};
        css.split(';').forEach(decl => {
            const [prop, ...valParts] = decl.split(':');
            if (prop && valParts.length) {
                result[prop.trim()] = valParts.join(':').trim();
            }
        });
        return result;
    };

    /* `transform` is applied via the inline style block (see getStyles), not as
       an SVG attribute on the root `<svg>` — Solid's SvgSVGAttributes type doesn't
       include `transform` and the attribute path is redundant with the style path. */
    return (
        <svg
            xmlns={props.xmlns}
            version={props.version}
            x={props.x}
            y={props.y}
            viewBox={props.viewBox}
            class={clsx('svg-icon', props.class, props.className)}
            style={{ ...getStyles(), ...(props.customCss ? parseCssString(props.customCss) : {}) }}
            aria-label={getAltText()}
            aria-hidden={props.ariaHidden || false}
            onClick={props.onClick}
            tabIndex={props.tabIndex}
        >
            {componentProps.children}
        </svg>
    );
};
